import React, {
  useState,
} from 'react';
import MailIcon from '@mui/icons-material/MailOutlineRounded';
import IconButton from '@mui/material/IconButton';
import {
  PatientPaymentTransactionViewModelDTO,
} from 'dtos';
import {
  PatientPaymentTransactionType,
} from 'dtos/patientPaymentTransactionType';
import ToolTip from 'core/components/Tooltip';
import Spinner from 'pages/Dashboard/components/Spinner';
import Tag from 'core/components/Tag';
import Name from 'pages/Dashboard/components/Name';
import Dialog from 'pages/Dashboard/components/Dialog';
import PaymentCell from 'pages/Dashboard/pages/Billing/Payments/columns/PaymentCell';
import AmountTag, {
  defaultNegativeClass,
} from 'pages/Dashboard/pages/Billing/AmountTag';
import PrinterIcon from 'core/icons/printer';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  formatDate,
} from 'utils/date';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import ReceiptEmailDialog from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/ReceiptEmailDialog';

export type PatientPaymentRowProps = {
  payment: PatientPaymentTransactionViewModelDTO;
};

export function PatientPaymentRow({ payment }: PatientPaymentRowProps) {
  const [printingInvoice, setPrintingInvoice] = useState<PatientPaymentTransactionViewModelDTO | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const {
    patientPaymentTransactionId,
    transactionMethod,
    userAlias,
    createdOn,
    createdByUser,
    type,
  } = payment ?? {};

  const isRefund = type === PatientPaymentTransactionType.Refund;

  return (
    <>
      <div
        key={patientPaymentTransactionId}
        className="grid grid-cols-[100px_120px_80px_100px_180px_auto] text-xs border-b border-gray-100 py-2 items-center"
      >
        <div>
          <Tag
            tagName={type}
            className="w-fit !px-2 !py-1 font-semibold"
          />
        </div>
        <div>
          <PaymentCell
            number={patientPaymentTransactionId?.toString() ?? '0'}
            type={transactionMethod?.discriminator ?? ''}
          />
        </div>
        <div>
          <AmountTag
            value={transactionMethod?.amount ?? 0}
            useColors
            classes={isRefund
              ? { positive: defaultNegativeClass }
              : undefined}
          />
        </div>
        <div>{formatDate(createdOn ?? '')}</div>
        <div className="w-44">
          <Name
            name={userAlias ?? getDisplayName(createdByUser?.formattedNames)}
          />
        </div>
        <div className="flex justify-end gap-1 px-1">
          <ToolTip title="Print Receipt">
            <span>
              <IconButton
                aria-label="Print Receipt"
                color="info"
                size="small"
                onClick={() => {
                  setPrintingInvoice(payment);
                  paymentsEventChannel.emit('onPaymentPrint', payment, () => {
                    setPrintingInvoice(null);
                  });
                }}
                className="disabled:opacity-50"
              >
                {patientPaymentTransactionId === printingInvoice?.patientPaymentTransactionId
                  ? <Spinner className="!w-4 !h-4" />
                  : <PrinterIcon className="!w-4 !h-4 !text-inherit" />}
              </IconButton>
            </span>
          </ToolTip>
          <ToolTip title="Email Receipt">
            <span>
              <IconButton
                aria-label="Email Receipt"
                color="info"
                size="small"
                onClick={() => { setEmailDialogOpen(true); }}
                className="disabled:opacity-50"
              >
                <MailIcon className="!w-4 !h-4 !text-inherit" />
              </IconButton>
            </span>
          </ToolTip>
        </div>
      </div>
      {emailDialogOpen && (
        <ReceiptEmailDialog
          payment={payment}
          onClose={() => { setEmailDialogOpen(false); }}
        />
      )}
    </>
  );
}

type ReceiptsProps = {
  data: PatientPaymentTransactionViewModelDTO[];
  onClose: () => void;
};

export default function Receipts({ data, onClose }: ReceiptsProps) {
  return (
    <Dialog
      title="Receipts"
      onClose={onClose}
      classes={{
        title: '!items-center',
      }}
      open
      className="w-fit !space-y-0"
    >
      <>
        <div className="grid grid-cols-[100px_120px_80px_100px_160px_auto] text-xs border-b border-gray-2200 text-gray-500 font-semibold py-2 items-end">
          <div className="text-left">Type</div>
          <div className="text-left">Tx Method</div>
          <div className="text-left">Amount</div>
          <div className="text-left">Created On</div>
          <div className="text-left">Created By</div>
          <div className="text-right" />
        </div>
        {data?.map((payment) => (
          <PatientPaymentRow
            key={payment.patientPaymentTransactionId ?? `${payment.createdOn}-${payment.type}-${payment.uniqueIdentifier}`}
            payment={payment}
          />
        ))}
      </>
    </Dialog>
  );
}
